import {Component, DestroyRef, inject, signal} from '@angular/core'
import {takeUntilDestroyed} from '@angular/core/rxjs-interop'
import {NavigationEnd, NavigationStart, Router} from '@angular/router'
import type {AppSession} from '@cart/src/app/entities/modals/app-session.model'
import {SplashScreen} from '@capacitor/splash-screen'
import {StatusBar} from '@capacitor/status-bar'
import {EdgeToEdge} from '@capawesome/capacitor-android-edge-to-edge-support'
import {IonApp, IonRouterOutlet, IonSplitPane, Platform} from '@ionic/angular/standalone'
import {locale} from 'moment'
import {UserTypeUve} from '@cart/src/app/entities/enums/user-type-uve.enum'
import {environment} from '@cart/src/environments/environment'
import {AppSessionService} from '@services/app-session.service'
import {AuthService} from '@services/auth.service'
import {BadgeNotificationService} from '@services/badge-notification.service'
import {CartService} from '@services/cart.service'
import {DeeplinkService} from '@services/deeplink.service'
import {DeployService} from '@services/deploy.service'
import {EventLoggerService} from '@services/event-logger.service'
import {InfoService} from '@services/info.service'
import {NavigationService} from '@services/navigation.service'
import {NetWatchService} from '@services/net-watch.service'
import {NotificationService} from '@services/notification.service'
import {TimingService} from '@services/timing.service'
import {UserFunctionsService} from '@services/user-functions.service'
import {UserProfileService} from '@services/user-profile.service'
import {UserService} from '@services/user.service'
import {LogType, Logger} from '@services/utils/logger'
import {VersionService} from '@services/version.service'
import {ChatReactivity} from '@uve-solutions/f-chat/reactivity'
import configStyle from '../theme/config-style.json'
import {LeftSideMenuComponent} from './components/shared/left-side-menu/left-side-menu.component'
import {AnalyticsEventTypes} from './entities/enums/analytics-event-types.enum'
import {ChangeOutletService} from './services/change-outlet.service'
import {ChatListService} from './services/chat-list.service'
import {SocketService} from './services/socket.service'
import {UserLanguageService} from './services/user-language.service'

// noinspection JSUnusedLocalSymbols
declare let window: any

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
declare let gtag: (property: string, value: any, configs?: any) => {}

@Component({
  selector: 'app-root',
  imports: [IonApp, IonSplitPane, IonRouterOutlet, LeftSideMenuComponent],
  standalone: true,
  templateUrl: './app.component.html',
})
export class AppComponent {
  protected readonly eventLoggerService = inject(EventLoggerService)
  protected readonly userLanguageService = inject(UserLanguageService)

  readonly #platform = inject(Platform)
  readonly #destroyRef = inject(DestroyRef)
  readonly #userService = inject(UserService)
  readonly #router = inject(Router)
  readonly #netWatchService = inject(NetWatchService)
  readonly #notificationService = inject(NotificationService)
  readonly #infoService = inject(InfoService)
  readonly #deployService = inject(DeployService)
  readonly #deeplinkService = inject(DeeplinkService)
  readonly #versionService = inject(VersionService)
  readonly #timingService = inject(TimingService)
  readonly #sessionService = inject(AppSessionService)
  readonly #authService = inject(AuthService)
  readonly #userProfileService = inject(UserProfileService)
  readonly #cartService = inject(CartService)
  readonly #changeOutletsService = inject(ChangeOutletService)
  readonly #userFunctionsService = inject(UserFunctionsService)
  readonly #navigationService = inject(NavigationService)
  readonly #badgeNotificationService = inject(BadgeNotificationService)
  readonly #chatListService = inject(ChatListService)
  readonly #socketService = inject(SocketService)
  readonly #chatReactivity = inject(ChatReactivity)

  protected readonly enableSidePanel = signal(false)

  constructor() {
    this.#chatReactivity.initialize()
    this.#deeplinkService.init()

    this.initializeApp().then()

    this.#netWatchService.init()
    this.#netWatchService.watch()

    this.#router.events.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(event => {
      this.setSidePanel()
      if (event instanceof NavigationStart) {
        this.#timingService.initTimer()
      }
      if (event instanceof NavigationEnd) {
        this.removeLoadingSpinner()
      }
    })
    this.injectGAScripts()

    Logger.setEventLoggerServiceOnLogger(this.eventLoggerService)

    if (!this.#infoService.isWeb()) {
      return
    }

    this.#navigationService.setUpWebBackButton('/home')
  }

  async initializeApp() {
    const startTime = new Date().getTime()
    await this.#platform.ready()
    this.#socketService.init()
    this.#versionService.init()
    this.enableStatusBar().then()

    if (location.pathname.includes('/redirect')) {
      return
    }
    void this.setupLanguage()
    await this.initializeUserData()
    this.eventLoggerService.logEvent(AnalyticsEventTypes.userProfileInitialized, {
      time: (new Date().getTime() - startTime) / 1000,
    })

    this.#userFunctionsService.getApiChatFunctions().pipe(takeUntilDestroyed(this.#destroyRef)).subscribe()
    this.#deployService.init().then()
    this.#notificationService.init().then()
    this.eventLoggerService.init().then()
    this.subscribeConversationEvents()
    this.checkAppVersion()

    this.#platform.resume.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(async () => {
      await this.#authService.initializeUserProfile()
      this.#userFunctionsService.getApiChatFunctions().pipe(takeUntilDestroyed(this.#destroyRef)).subscribe()
      this.#badgeNotificationService.clearBadgeNumber()
      this.#chatListService.refreshContacts.emit(true)
    })

    this.hideSplashScreen()
  }

  injectGAScripts() {
    if (!this.#infoService.isWeb() || !environment.analytics || !environment.googleAnalyticsId) {
      return
    }

    const script = document.createElement('script')
    script.crossOrigin = 'anonymous'
    script.onload = () => {
      gtag('js', new Date())
      gtag('config', environment.googleAnalyticsId, {
        anonymize_ip: true,

        send_page_view: false,
      })
    }
    script.src = `https://www.googletagmanager.com/gtag/js?id=${environment.googleAnalyticsId}`
    script.async = true

    document.getElementsByTagName('head')[0].appendChild(script)
  }

  injectAdyenPaymentScripts() {
    const script = document.createElement('script')
    if (environment.production) {
      script.src = 'https://checkoutshopper-live.adyen.com/checkoutshopper/sdk/3.8.1/adyen.js'
      script.crossOrigin = 'anonymous'
      script.integrity = 'sha384-pLfJ6XKllmblOK86IVevGarh2cfeBr6lWAEkumlMA3hgTqKpEgNn8ID7zq4HsC6H'
    } else {
      script.src = 'https://checkoutshopper-test.adyen.com/checkoutshopper/sdk/3.8.1/adyen.js'
      script.crossOrigin = 'anonymous'
      script.integrity = 'sha384-rGkZxBRhUfCECwNuNrllGf8dOpeUsKTMqczAkphoWMne67C32+na/29lUGT3HVOB'
    }

    document.getElementsByTagName('head')[0].appendChild(script)
  }

  async setupLanguage() {
    Logger.log(LogType.debug, 'Setting up language...')
    const currentLang = await this.userLanguageService.initLanguage()
    const currentLangMoment = currentLang && currentLang !== 'en' ? currentLang : 'en-gb'
    locale(currentLangMoment)
  }

  setSidePanel() {
    this.enableSidePanel.set(
      this.#router.url !== '/' &&
        this.#router.url !== '/signup' &&
        this.#router.url !== '/login' &&
        this.#router.url !== '/login/guest' &&
        this.#router.url !== '/auth' &&
        this.#router.url !== '/forgot-password' &&
        this.#router.url !== '/forgot-password-otp' &&
        this.#router.url !== '/unknown-error' &&
        this.#router.url !== '/locate' &&
        this.#router.url.indexOf('/auth/redirect') === -1
    )
  }

  async enableStatusBar(): Promise<void> {
    Logger.log(LogType.debug, 'Enabling status bar...')
    if (!this.#infoService.isWeb()) {
      let primaryColor: string = this.getPrimaryColor() || '#3C3C3C'

      if (primaryColor?.length === 9 && primaryColor.startsWith('#')) {
        primaryColor = primaryColor.substring(0, 7)
      }

      await this.setEdgeToEdgeBackgroundColor(primaryColor)
      await this.setStatusBarBackgroundColor(primaryColor)
    }
  }

  getPrimaryColor(): string {
    return configStyle.backgroundHeader
  }

  async setEdgeToEdgeBackgroundColor(primaryColor: string): Promise<void> {
    if (this.#infoService.isAndroid()) {
      try {
        return EdgeToEdge.setBackgroundColor({
          color: primaryColor,
        })
      } catch (error) {
        Logger.log(LogType.error, 'Error setting edge to edge background color', error)
      }
    }
  }

  async setStatusBarBackgroundColor(primaryColor: string): Promise<void> {
    if (this.#infoService.isAndroid()) {
      try {
        return StatusBar.setBackgroundColor({
          color: primaryColor,
        })
      } catch (error) {
        Logger.log(LogType.error, 'Error setting status bar background color', error)
      }
    }
  }

  async initializeUserData(): Promise<void> {
    Logger.log(LogType.debug, 'Initializing user data...')
    const originalUser: AppSession = await this.#userService.getOriginalUserDetails()
    const userLocalStorage = this.#userService.getUserFromStorage()
    if (!originalUser && userLocalStorage) {
      Logger.log(LogType.error, 'Inconsistent Storage. Cleaning up...')
      this.#sessionService.logout()
      return
    }
    this.#userService.setUser(originalUser)
    await this.#userProfileService.loadProfileFromStorage()

    this.#sessionService.fetchProfile().pipe(takeUntilDestroyed(this.#destroyRef)).subscribe()
    this.#sessionService.userEnvironments().pipe(takeUntilDestroyed(this.#destroyRef)).subscribe()
    this.#sessionService.storeRootSession(originalUser)
  }

  private hideSplashScreen() {
    Logger.log(LogType.debug, 'Hiding splash screen...')
    if (!this.#infoService.isWeb()) {
      SplashScreen.hide().then()
    }
  }

  private subscribeConversationEvents() {
    this.#cartService.onGetCart().pipe(takeUntilDestroyed(this.#destroyRef)).subscribe()

    this.#changeOutletsService
      .onEditOutletEvent()
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => {
        const user = this.#userService.getUser()
        if (user.type === UserTypeUve.CUSTOMER_UVE) {
          this.#authService.redirectIfUnBind()
        }
      })
  }

  private checkAppVersion() {
    Logger.log(LogType.debug, 'Checking app version...')
    this.#versionService.checkVersionCompatibility().then()
  }

  private removeLoadingSpinner() {
    Logger.log(LogType.debug, 'Removing loading spinner...')
    const loadingElement = document.getElementById('app-loading-placeholder-spinner')
    loadingElement?.remove()
  }
}
